import { transformObjectToArray } from '@helpers'

import { INITIAL_VAT_PERIODS } from '@constants'

import authActions from '../auth/actions'
import actions from './actions'
import type { DashboardReducerAction } from './types'

function serializeCountries(countries: Record<string, string>): Country[] {
  return Object.keys(countries).map(key => ({
    id: key,
    name: countries[key],
  }))
}

function serializeDashboardCounts({
  new: _new,
  expiring,
  expired,
  is_updated,
}: {
  new?: number
  expiring?: number
  expired?: number
  is_updated?: number
}) {
  return {
    is_new: _new || 0,
    is_expire: expiring || 0,
    is_expired: expired || 0,
    is_updated: is_updated || 0,
  }
}

const initialCommonState: DashboardCommon = {
  acceptedFileMimeTypes: undefined,
  artifact_max_size: 0,
  bank_providers: [],
  hubspot_event_actions: [],
  company_types: [],
  countries: [],
  currencies: [],
  document_max_size: 0,
  income_types: [],
  industries: [],
  initializing: false,
  payment_methods: [],
  vat_areas: [],
  vat_periods: [],
  taxation: {
    atalany_cost_percentages: [],
    atalany_employment_types: [],
    chamber_membership_obligations: [],
    company_szja_discount_type: [],
    inactivity_types: [],
    income_brackets: [],
    name_titles: [],
    other_benefits: [],
    savos_ipa_types: [],
    sexes: [],
    tax_forms: [],
  },
}

export const initialState: DashboardStore = {
  approverUsers: [],
  common: initialCommonState,
  counts: {
    ...serializeDashboardCounts({}),
    processing: 0,
    show_szamlazz_sync_ribbon: false,
  },
  ledgerNumbers: [],
  tags: {
    data: [],
    loading: false,
    error: null,
    fetched: false,
  },
  taxCodes: {
    data: [],
    loading: false,
    error: null,
    fetched: false,
  },
  xmlUploading: false,
}

export default function reducer(state: DashboardStore = initialState, action: DashboardReducerAction): DashboardStore {
  switch (action.type) {
    case actions.initialize.REQUEST:
      return {
        ...state,
        common: {
          ...state.common,
          initializing: true,
        },
      }

    case actions.initialize.SUCCESS: {
      const {
        accepted_content_types,
        banks,
        countries,
        income_types,
        payment_methods,
        vat_areas,
        vat_periods = INITIAL_VAT_PERIODS,
        ...rest
      } = action.payload

      return {
        ...state,
        common: {
          ...state.common,
          initializing: false,
          ...rest,
          acceptedFileMimeTypes: accepted_content_types,
          bank_providers: banks,
          countries: serializeCountries(countries),
          income_types: transformObjectToArray(income_types, true) as CommonValueAndLabelType[],
          payment_methods: transformObjectToArray(payment_methods) as CommonStringValueAndLabelType[],
          vat_areas: transformObjectToArray(vat_areas) as CommonStringValueAndLabelType[],
          vat_periods: transformObjectToArray(vat_periods) as CommonStringValueAndLabelType[],
        },
      }
    }

    case actions.initialize.FAILURE:
      return {
        ...state,
        common: {
          ...state.common,
          initializing: false,
        },
      }

    // Expenses
    case actions.dashboardStatusCheck.SUCCESS: {
      const { processing, show_szamlazz_sync_ribbon } = action.payload
      if (
        processing !== state.counts.processing ||
        show_szamlazz_sync_ribbon !== state.counts.show_szamlazz_sync_ribbon
      ) {
        return {
          ...state,
          counts: {
            ...state.counts,
            processing,
            show_szamlazz_sync_ribbon,
          },
        }
      } else {
        return state
      }
    }

    // TAGS
    case actions.fetchTags.REQUEST:
      return {
        ...state,
        tags: {
          ...state.tags,
          loading: true,
          fetched: false,
        },
      }

    case actions.fetchTags.SUCCESS:
      return {
        ...state,
        tags: {
          data: action.payload,
          loading: false,
          fetched: true,
          error: null,
        },
      }

    case actions.fetchTags.FAILURE:
      return {
        ...state,
        tags: {
          ...state.tags,
          loading: false,
          error: action.payload,
        },
      }

    case actions.createTag.SUCCESS:
      return {
        ...state,
        tags: {
          ...state.tags,
          data: [...state.tags.data, action.payload],
        },
      }

    //* reset data when change company
    case authActions.selectCompany.SUCCESS:
      return {
        ...state,
        tags: {
          ...initialState.tags,
        },
        ledgerNumbers: [],
      }

    // SALARY
    case actions.uploadSalaryAndTaxV2.REQUEST:
      return {
        ...state,
        xmlUploading: true,
      }

    case actions.uploadSalaryAndTaxV2.SUCCESS:
    case actions.uploadSalaryAndTaxV2.FAILURE:
      return {
        ...state,
        xmlUploading: false,
      }

    // TAX CODES
    case actions.fetchTaxCodes.REQUEST:
      return {
        ...state,
        taxCodes: {
          ...state.taxCodes,
          loading: true,
          fetched: false,
        },
      }

    case actions.fetchTaxCodes.SUCCESS:
      return {
        ...state,
        taxCodes: {
          data: action.payload,
          loading: false,
          fetched: true,
          error: null,
        },
      }

    case actions.fetchTaxCodes.FAILURE:
      return {
        ...state,
        taxCodes: {
          ...state.taxCodes,
          loading: false,
          error: action.payload,
        },
      }

    case actions.fetchLedgerNumbers.SUCCESS:
      return {
        ...state,
        ledgerNumbers: action.payload,
      }

    case actions.createLedgerNumber.SUCCESS: {
      const exists = state.ledgerNumbers.find(ledgerNumber => ledgerNumber.id === action.payload.id)
      if (!exists) {
        return {
          ...state,
          ledgerNumbers: [...state.ledgerNumbers, action.payload], // push only when not exists
        }
      }
      return state
    }

    case actions.fetchApproverUsers.SUCCESS:
      return {
        ...state,
        approverUsers: action.payload
          .filter(({ deny_approve_expense_permission }) => !deny_approve_expense_permission)
          .map(({ user, email }) => ({ id: user, name: email })),
      }

    default:
      return state
  }
}
